import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  static openClass = 'menu-drawer-is-open';

  /** @type {ResizeObserver | undefined} */
  #visibilityObserver;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown, true);
    // toggle 不冒泡，用捕获阶段监听整个抽屉内的手风琴开合
    this.addEventListener('toggle', this.#onAccordionToggle, true);
    this.#setupAnimatedElementListeners();

    const header = this.closest('#header-component');
    const container = this.closest('.header__drawer');
    if (header?.querySelector('.gc-editorial-nav') && container) {
      // Follow actual CSS visibility, including the editorial tablet breakpoint.
      // Closing normally lets details' toggle release only this drawer's scroll lock.
      this.#visibilityObserver = new ResizeObserver(() => {
        if (this.isOpen && !container.getClientRects().length) this.close();
      });
      this.#visibilityObserver.observe(container);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.removeEventListener('keydown', this.#onKeyDown, true);
    this.removeEventListener('toggle', this.#onAccordionToggle, true);
    document.documentElement.classList.remove(HeaderDrawer.openClass);
  }

  /**
   * 一级手风琴单开模式：打开一个时自动收起同级其它已开的；同时同步 summary 的 aria-expanded。
   * @param {Event} event
   */
  #onAccordionToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details === this.refs.details) return;
    if (!details.closest('accordion-custom')) return;

    // 同步自身 aria-expanded（开与关都同步）
    details.querySelector('summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (!details.open) return;

    const menu = details.closest('.menu-drawer__menu.has-submenu');
    if (!menu) return;

    menu.querySelectorAll(':scope > li accordion-custom > details[open]').forEach((other) => {
      if (other === details) return;
      other.open = false;
      other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    if (target.closest('dialog, [role="dialog"]')) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat) return;
    const details = target.closest('details[open]');
    this.#close(details && this.contains(details) ? details : this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add(HeaderDrawer.openClass);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (!details.open || !this.isConnected) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (details.open && details.classList.contains('menu-open')) trapFocus(details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Open the cart using the same trigger as the header cart button.
   * @param {Event} [event]
   */
  openHeaderCart(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.close();

    window.requestAnimationFrame(() => {
      const headerCartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (headerCartTrigger instanceof HTMLElement) {
        headerCartTrigger.click();
        return;
      }

      window.location.href = '/cart';
    });
  }

  /**
   * Open account/login using the same header account entry.
   * @param {Event} [event]
   */
  openHeaderAccount(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.close();

    window.setTimeout(async () => {
      await customElements.whenDefined('shopify-account');

      const headerAccount = document.querySelector('header-actions shopify-account');
      if (headerAccount && typeof headerAccount.showPanel === 'function') {
        headerAccount.showPanel();
        return;
      }

      const nativeSignedOutTrigger = document.querySelector('header-actions .account-button [slot="signed-out-avatar"]');
      if (nativeSignedOutTrigger instanceof HTMLElement) {
        nativeSignedOutTrigger.click();
        return;
      }

      if (headerAccount instanceof HTMLElement) {
        headerAccount.click();
        return;
      }

      window.location.href = '/account/login';
    }, 180);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    if (!details.open) return;
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details !== this.refs.details && details.closest('accordion-custom')) {
      reset(details);
      trapFocus(this.refs.details);
      summary.focus({ preventScroll: true });
      return;
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          document.documentElement.classList.remove(HeaderDrawer.openClass);
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          if (this.contains(document.activeElement) || document.activeElement === document.body) {
            const focusTarget = summary.getClientRects().length
              ? summary
              : this.closest('#header-component')?.querySelector('.gc-editorial-nav a[href]');
            if (focusTarget?.getClientRects().length) focusTarget.focus({ preventScroll: true });
          }
        } else {
          trapFocus(this.refs.details);
          summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        content.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.classList.remove('details-content--no-animation');
          }
        });
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
